import { Map as ImMap, Record, type RecordOf, isRecord } from 'immutable';
import type { Action, ActionFunctionAny } from 'redux-actions';
import { getCommonConfig, getLogger } from '../../utils/core';

const logger = getLogger('resourceStoreTypes');

export function getId<R extends { id?: string | null; clientId?: string | null }>(resource: R): string {
  const id = resource.id || resource.clientId;
  if (!id) {
    throw new Error('Trying to use an id from a resource which is missing both clientId and id');
  }
  return id;
}

export type QcsResourcesResponse<T> = {
  resources: T[];
  metaData?: {
    pageSize?: number;
    limit?: number;
    asOf: string;
    currentPage: number;
    offset: number;
    totalPages: number;
    totalSize: number;
  };
};

export type BaseResource = {
  clientId?: string | null;
  id?: string | null;
  isDeleted?: boolean | null;
};

export type QcsSyncResourceStoreProps<T extends BaseResource> = {
  resourcesById: ImMap<string, RecordOf<T>>;

  resourceName?: string | null;
  lastSyncDate?: string | null;
  loadPending: boolean;
  isLoading: boolean;
  error?: any;
};
export type QcsSyncResourceStore<
  T extends BaseResource,
  V extends QcsSyncResourceStoreProps<T> = QcsSyncResourceStoreProps<T>,
> = RecordOf<V>;

export function createTypedQcsSyncResourceStoreFactory<
  T extends BaseResource,
  V extends QcsSyncResourceStoreProps<T> = QcsSyncResourceStoreProps<T>,
>(defaults?: Partial<V>) {
  return Record<V>({
    resourcesById: ImMap(),
    resourceName: null,
    lastSyncDate: null,
    loadPending: true,
    isLoading: false,
    error: undefined,
    ...defaults,
  } as V);
}

// Deprecated, use createTypedQcsSyncResourceStoreFactory instead if you can
export const mkQcsSyncResourceStore = Record<QcsSyncResourceStoreProps<any>>({
  resourcesById: ImMap(),

  resourceName: null,
  lastSyncDate: null,
  loadPending: true,
  isLoading: false,
  error: undefined,
});

export type QcsSyncResourcesPayloadProps<T extends BaseResource> = {
  asyncDispatch?: any;
  resources: RecordOf<T>[];
  doMerge?: boolean;
  replaceCollection?: boolean;
  lastSyncDate?: string | null;
  error?: any;
};
export type QcsSyncResourcesPayload<T extends BaseResource> =
  | QcsSyncResourcesPayloadProps<T>
  | RecordOf<QcsSyncResourcesPayloadProps<T>>;
export function createTypedQcsSyncResourcePayloadFactory<T extends BaseResource>() {
  return Record<QcsSyncResourcesPayloadProps<T>>({
    asyncDispatch: undefined,
    resources: [],
    doMerge: true,
    replaceCollection: false,
    lastSyncDate: null,
    error: undefined,
  });
}
export const mkQcsSyncResourcesPayload = Record<QcsSyncResourcesPayloadProps<any>>({
  asyncDispatch: undefined,
  resources: [],
  doMerge: true,
  replaceCollection: false,
  lastSyncDate: null,
  error: undefined,
});

export type PollConfigProps = {
  isComplete: (error: any, attempt: number) => boolean;
  attempts: number;
  pause: number;
};
export type PollConfig = RecordOf<PollConfigProps>;
export const mkPollConfig = Record<PollConfigProps>({
  isComplete: () => false,
  attempts: 10,
  pause: 300,
});

// FIXME: redefine this type based on PayloadAction from redux toolkit
export type CoreAction<Payload, Meta> = Action<Payload> & {
  meta?: Meta;
  asyncDispatch?: (action: CoreAction<Payload, Meta>) => void;
};

export type CoreActionCreator<Payload, Meta> = ActionFunctionAny<CoreAction<Payload, Meta>>;

export type QcsError = {
  title: string;
  httpStatus: number;
  code: string;
  extData: any;
};

export type QcsSyncResourcesConfigProps<T extends BaseResource> = {
  resourceName: string;
  resourceBaseUrl: string | ((action: any) => string);
  axiosConfig: any;
  getLastSyncDate?: ((val: any) => any) | null;
  pollConfig: PollConfig;
  applyResourceFromCRUDResponse?: boolean;
  handleRetry: boolean | ((error: any, action: CoreAction<any, any>, config: QcsSyncResourcesConfig<T>) => boolean);
  ignoreError?:
    | ((error: any, action: CoreAction<any, any>, config: QcsSyncResourcesConfig<T>) => boolean | string)
    | boolean;

  transformClientResourceToRequestData: (resource: RecordOf<T> | T) => T;
  transformResponseToResource: (response: { data: T }) => RecordOf<T> | null | undefined;
  transformResponseToResources: (response?: { data?: { resources?: T[] } } | null) => RecordOf<T>[] | null | undefined;
  transformResourceWithResponse: (
    resource: RecordOf<T> | T,
    response: { data?: T & { resource?: T; resources?: T[]; correlationIds?: { [id: string]: string } } },
    config: QcsSyncResourcesConfig<T>,
  ) => RecordOf<T> | Array<RecordOf<T>> | T | null | undefined;
  transformResourcesWithResponse: (
    resources: Array<RecordOf<T> | T>,
    response: {
      data?: T & { items?: Array<{ resource?: T & { resource?: T; status?: string }; errors?: QcsError[] }> };
    },
  ) => Array<RecordOf<T> | T | undefined> | undefined | null;
  transformResourceToRequestData: (resource: RecordOf<T> | T) => T;
  transformResourcesToRequestData: (resources: Array<RecordOf<T> | T>) => { resources: T[] };
  transformServerResourceToClientResource: (data: T) => RecordOf<T> | null | undefined;

  successAction?: CoreActionCreator<any, any>;
  failureAction?: CoreActionCreator<any, any>;
};
export type QcsSyncResourcesConfig<T extends BaseResource> = RecordOf<QcsSyncResourcesConfigProps<T>>;

const getQcsSyncResourcesConfigDefaults = <T extends BaseResource>(): QcsSyncResourcesConfigProps<T> => ({
  resourceName: 'unknown-resource',
  resourceBaseUrl: 'resources',
  axiosConfig: undefined,
  getLastSyncDate: undefined,
  pollConfig: mkPollConfig(),
  applyResourceFromCRUDResponse: undefined,
  handleRetry: getCommonConfig()?.handle_retry,
  ignoreError: undefined,

  // main transformers to override
  transformServerResourceToClientResource: (data) => data && Record(data)(data),
  transformClientResourceToRequestData: (resource) => (isRecord(resource) ? (resource.toJS() as T) : resource),

  // framework transformers that generally shouldn't need to change
  transformResponseToResource(response) {
    // TODO: all other response handling code looks for response.data.resource. Are we sure there shouldn't be a resource field?
    return this.transformServerResourceToClientResource(response.data);
  },
  transformResponseToResources(response) {
    const clientResources: RecordOf<T>[] = [];

    response?.data?.resources?.forEach((r) => {
      const clientResource = this.transformServerResourceToClientResource(r);
      if (clientResource) clientResources.push(clientResource);
    });

    return clientResources;
  },
  transformResourceWithResponse(resource, response, config) {
    if (response?.data?.resource && config?.applyResourceFromCRUDResponse == null) {
      logger.warn(
        `${config?.resourceName}: enable/disable "response" property handling explicitly (applyResourceFromCRUDResponse = true/false)`,
      );
    }
    if (response?.data?.resource && config?.applyResourceFromCRUDResponse) {
      // apply resource
      if ('merge' in resource && typeof resource.merge === 'function') {
        const fieldsToMerge = this.transformServerResourceToClientResource({
          ...response.data.resource,
          clientId: response.data.clientId || response.data.resource.clientId,
        });

        return fieldsToMerge ? resource.merge(fieldsToMerge) : resource;
      }

      return {
        ...resource,
        ...response?.data?.resource,
        clientId: response?.data?.clientId || response?.data?.resource?.clientId,
      };
    }
    if (response?.data?.id) {
      // apply id
      return 'set' in resource && typeof resource.set == 'function'
        ? resource.set('id', response?.data?.id)
        : { ...resource, id: response?.data?.id };
    }
    return resource;
  },
  transformResourcesWithResponse(resources, response) {
    const errors: string[] = [];
    const newResources = response?.data?.items?.map((item) => {
      let resource: T | RecordOf<T> | undefined;
      if (item.resource?.status === 'ERROR' && item.errors) {
        errors.push(...item.errors.map((e) => JSON.stringify(e)));
      }
      const resourceItem = item.resource;
      const resourceUpdate = resourceItem?.resource;
      const clientId = resourceItem?.clientId || resourceUpdate?.clientId;
      const id = resourceItem?.id || resourceUpdate?.id;
      if (clientId) {
        resource = resources.find((fResource) => fResource.clientId === clientId);
        if (resource) {
          if (resourceUpdate) {
            resource =
              'merge' in resource && typeof resource.merge === 'function'
                ? resource.merge(this.transformServerResourceToClientResource({ ...resourceUpdate, clientId })!) // transformer only returns null/undefined if input is null/undefined, we've already guarded against that
                : { ...resource, ...resourceUpdate, clientId };
          } else if (id) {
            resource =
              'set' in resource && typeof resource.set === 'function' ? resource.set('id', id) : { ...resource, id };
          }
        } else {
          // eslint-disable-next-line no-console
          console.error('transformResourcesWithResponse: clientId not found');
        }
      } else {
        resource = resources.find((fResource) => fResource.id === id);
      }
      return resource;
    });

    if (errors.length > 0) {
      throw errors;
    }

    return newResources;
  },

  // request transformers
  transformResourceToRequestData(resource) {
    return this.transformClientResourceToRequestData(resource);
  },
  transformResourcesToRequestData(resources) {
    return {
      resources: resources.reduce<Array<RecordOf<T> | T>>((list, resource) => {
        list.push(this.transformClientResourceToRequestData(resource));
        return list;
      }, []),
    };
  },

  successAction: undefined,
  failureAction: undefined,
});

export function createTypedQcsSyncResourcesConfigFactory<T extends BaseResource>() {
  return Record<QcsSyncResourcesConfigProps<T>>(getQcsSyncResourcesConfigDefaults<T>());
}
export const mkQcsSyncResourceConfig = Record<QcsSyncResourcesConfigProps<any>>(
  getQcsSyncResourcesConfigDefaults<any>(),
);
